import { Zap } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskType,
  TASK_TEMPLATE_TYPES,
} from '@/services/types/task-types';

interface TaskActionConfig {
  label: string;
  /** 'modal' opens the action modal; 'link' navigates directly */
  mode: 'modal' | 'link' | 'request_modal';
  icon?: 'zap' | 'none';
}

/** TRR statuses that represent the pre-submission stage. */
const SUBMIT_STAGE_STATUSES = new Set(['not_ready', 'pending']);

export function isSubmitRequestTask(task: Task) {
  // If the treatment record request has progressed past the submission stage,
  // this is a follow-up task, not a submit-request task.
  const trrStatus = task.treatment_record_request?.status;
  if (trrStatus && !SUBMIT_STAGE_STATUSES.has(trrStatus)) {
    return false;
  }

  const taskTypeName =
    typeof task.type === 'string' ? task.type : (task.type?.name ?? task.name);
  const normalizedName = (taskTypeName ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  return Boolean(
    task.treatment_record_request?.id &&
      (task.template_type === TASK_TEMPLATE_TYPES.TREATMENT_RECORD_REQUEST ||
        normalizedName.includes('submitrequest')),
  );
}

function getTaskActionConfig(task: Task): TaskActionConfig | null {
  const canonicalType =
    typeof task.type === 'object'
      ? (task.type?.canonical_type as CanonicalTaskType | undefined)
      : undefined;

  // Claims review tasks -> deep link directly
  if (
    canonicalType === CanonicalTaskType.REVIEW_FOUND_COVERAGE ||
    canonicalType === CanonicalTaskType.OPEN_CLAIM ||
    canonicalType === CanonicalTaskType.SEND_LOR ||
    canonicalType === CanonicalTaskType.LOR_ACKNOWLEDGEMENT
  ) {
    return { label: 'Go to Claims Review', mode: 'link' };
  }

  // Premises research task -> deep link to research property page
  if (canonicalType === CanonicalTaskType.RESEARCH_PROPERTY_OWNERS) {
    return { label: 'Go to Research Property', mode: 'link' };
  }

  if (canonicalType === CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES) {
    return { label: 'Go to Review Incident Properties', mode: 'link' };
  }

  if (canonicalType === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
    return { label: 'Open Claim Step', mode: 'link' };
  }

  if (canonicalType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) {
    return { label: 'Continue Research', mode: 'link' };
  }

  // Initial review
  if (canonicalType === CanonicalTaskType.CONDUCT_INITIAL_REVIEW) {
    return { label: 'Start Initial Review', mode: 'modal' };
  }

  if (isSubmitRequestTask(task)) {
    return {
      label: 'Configure & Submit Requests',
      mode: 'request_modal',
      icon: 'none',
    };
  }

  // Medical records follow-up (Voice AI tasks)
  if (
    task.treatment_record_request?.id &&
    (task.medical_note_id || task.treatment_record_request?.provider)
  ) {
    return { label: 'Follow Up with Provider', mode: 'modal', icon: 'zap' };
  }

  // Insurance group with companions
  const isInsuranceGroupTask =
    task.group?.canonical_group === CanonicalTaskGroup.FIRST_PARTY_INSURANCE ||
    task.group?.canonical_group === CanonicalTaskGroup.THIRD_PARTY_INSURANCE;
  if (isInsuranceGroupTask && (task.companion_count ?? 0) > 1) {
    return { label: 'Review Group Coverages', mode: 'modal' };
  }

  // Verify 1P Coverage
  if (task.template_type === TASK_TEMPLATE_TYPES.COVERAGE_VERIFY_1P_COVERAGE) {
    return { label: 'Verify 1P Coverage', mode: 'modal' };
  }

  // Client portal upload review
  if (task.template_type === TASK_TEMPLATE_TYPES.CLIENT_PORTAL_REVIEW_UPLOADS) {
    return { label: 'Review Uploaded Documents', mode: 'modal' };
  }

  // Open File (correspondence actions)
  if (canonicalType === CanonicalTaskType.OPEN_FILE) {
    return { label: 'Send Notice', mode: 'modal' };
  }

  // Treatment check-in tasks
  const taskTypeName =
    typeof task.type === 'string' ? task.type : (task.type?.name ?? task.name);
  const normalizedName = (taskTypeName ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (
    canonicalType === CanonicalTaskType.TREATMENT_CHECK_IN ||
    normalizedName.includes('treatmentcheckin') ||
    (task.name && task.name.toLowerCase().includes('treatment check'))
  ) {
    return { label: 'Go to Check-In', mode: 'modal' };
  }

  // SMS-eligible tasks (document collection, health insurance card, HIPAA directive)
  if (
    canonicalType === CanonicalTaskType.HEALTH_INSURANCE_CARD ||
    canonicalType === CanonicalTaskType.HIPAA_DIRECTIVE ||
    taskTypeName === 'Document Collection'
  ) {
    return { label: 'Send SMS', mode: 'modal' };
  }

  return null;
}

interface TaskActionButtonProps {
  task: Task;
  onClick: () => void;
  /** Override the button label (e.g. when the agent has an active state) */
  overrideLabel?: string;
  /** Show a colored dot before the label */
  overrideDotClassName?: string;
}

export const TaskActionButton: React.FC<TaskActionButtonProps> = ({
  task,
  onClick,
  overrideLabel,
  overrideDotClassName,
}) => {
  const config = getTaskActionConfig(task);
  if (!config) return null;

  const label = overrideLabel ?? config.label;
  const showDot = Boolean(overrideDotClassName);
  const showIcon = !overrideLabel && config.icon !== 'none';

  return (
    <Button
      type="button"
      onClick={onClick}
      size="sm"
      className="h-8 w-full justify-center rounded-xl bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-800"
    >
      {showDot && (
        <span className={cn('mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full', overrideDotClassName)} />
      )}
      {showIcon && <Zap className="mr-1.5 h-3 w-3" />}
      {label}
    </Button>
  );
};

export { getTaskActionConfig };
